// Composables
import AuthService from '@core/services/AuthService';
import ModulesService from '@core/services/ModulesService';
import {sanitizeInternalRedirect} from '@core/utils/sanitizeInternalRedirect';
import {internalDevPortalRoutes} from '@internalDevportal/router/internalDevportal.routes';
import {routes as routesFromModule} from '@core/router/routes';
import {createRouter, createWebHistory, RouteLocationRaw} from 'vue-router';
import ConfigProvider from '@core/services/ConfigProvider';
import {Guard, isPublicRoute} from '@core/router/guard';

try {
  await ModulesService.getModules(false);
} catch (error) {
  console.error('Failed to load modules configuration', error);
}

const portfolioModuleEnabled =
  ConfigProvider.value('portfolioModuleEnabled') !== 'false';
const dataDictionaryModuleEnabled =
  ConfigProvider.value('dataDictionaryModuleEnabled') !== 'false';
const subscriptionManagerModuleEnabled =
  ConfigProvider.value('subscriptionManagerModuleEnabled') !== 'false';

const dataDictionaryRoutes = dataDictionaryModuleEnabled
  ? (await import('@dataDictionary/router/dataDictionary.routes'))
      .dataDictionaryRoutes
  : [];

const portfolioRoutes = portfolioModuleEnabled
  ? (await import('@portfolio/router/portfolio.routes')).portfolioRoutes
  : [];

const subscriptionManagerRoutes = subscriptionManagerModuleEnabled
  ? (await import('@subscriptionManager/router/subscriptionManager.routes'))
      .subscriptionManagerRoutes
  : [];

const domainModuleEnabled =
  portfolioModuleEnabled || dataDictionaryModuleEnabled;
const domainRoutes = domainModuleEnabled
  ? (await import('@domain/router/domain.routes')).domainRoutes
  : [];

const client = ConfigProvider.value('client');
const routes = [...routesFromModule];
if (ModulesService.externalDeveloperPortalEnabled === false || !client) {
  routes.push({
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@core/components/NotFoundPage.vue'),
    meta: {core: true},
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    ...routes,
    {
      path: '/callback',
      component: {
        render: () => null,
        async mounted() {
          const ok = await AuthService.handleCallback();
          if (!ok) {
            return router.replace({path: '/login'});
          }
          let redirectPath: RouteLocationRaw = {path: '/dashboard'};
          if (!ModulesService.internalDeveloperPortalEnabled) {
            if (portfolioModuleEnabled && ModulesService.portfolioEnabled) {
              redirectPath = {name: 'portfolioDashboard'};
            } else if (
              dataDictionaryModuleEnabled &&
              ModulesService.dataDictionaryEnabled
            ) {
              redirectPath = {name: 'DataDictionaryDashboard'};
            }
          }
          const safe = sanitizeInternalRedirect(this.$route.query.redirect);
          return router.push(safe ?? redirectPath);
        },
      },
    },
  ],
});

if (ModulesService.externalDeveloperPortalEnabled && client) {
  // Dynamic import to prevent Vite from analyzing client-specific routes during pre-bundling
  const externalDeveloperPortalRoutes = (
    await import('@externalDevportal/externalDevportal.routes')
  ).default;
  router.addRoute({
    path: '/',
    component: () => {
      return import('@client/layouts/ClientLayout.vue');
    },
    children: [
      ...routes,
      ...internalDevPortalRoutes,
      ...dataDictionaryRoutes,
      ...portfolioRoutes,
      ...domainRoutes,
      ...subscriptionManagerRoutes,
      ...externalDeveloperPortalRoutes,
    ],
  });
} else {
  router.addRoute({
    path: '/',
    component: () => import('../../layouts/default/DefaultLayout.vue'),
    children: [
      ...routes,
      ...internalDevPortalRoutes,
      ...dataDictionaryRoutes,
      ...portfolioRoutes,
      ...domainRoutes,
      ...subscriptionManagerRoutes,
    ],
  });
}

const CALLBACK_PATHS = [
  '/callback',
  '/callback_silent',
  '/callback_logout',
] as const;

const CALLBACK_PATH_SET: ReadonlySet<string> = new Set(CALLBACK_PATHS);

const guard = new Guard();

router.beforeEach(async to => {
  // Skip version check for known OAuth callback routes only (not arbitrary paths containing "callback")
  if (CALLBACK_PATH_SET.has(to.path)) {
    return;
  }

  if (!isPublicRoute(to)) {
    await guard.checkVersion();
  }

  const dashboard = guard.handleDashboardRoutes(to);
  if (dashboard !== undefined) return dashboard;

  const auth = await guard.handleAuthentication(to);
  if (auth !== undefined) return auth;

  const onboarding = await guard.redirectToOnboarding(to);
  if (onboarding !== undefined) return onboarding;

  await guard.handleModules(to);

  const notFound = await guard.handleNotFound(to);
  if (notFound !== undefined) return notFound;

  if (guard.shouldRedirectToNoEnvironmentAccess(to)) {
    return {name: 'noEnvironmentAccess'};
  }
});

export default router;
export {guard as routeGuard};
